<template>
  <div class="asc-timeline-chart">
    <asc-spinner-wrapper :loading="loading">
      <div v-if="!isEmpty" class="asc-timeline-chart--container">
        <asc-chart :options="getChartOptions"></asc-chart>
      </div>
      <asc-no-data v-if="isEmpty"></asc-no-data>
      <template v-if="flags.length > 0">
        <asc-filter-legend
          class="asc-plot-time-chart--legend"
          :items="getLegendItems"
          :on-item-click="handleLegendItemClick"
          :items-selected="legendItemsSelected"
        ></asc-filter-legend>
      </template>
    </asc-spinner-wrapper>
  </div>
</template>

<script>
import AscSpinnerWrapper from '@/components/atoms/AscSpinnerWrapper'
import AscFilterLegend from '@/components/molecules/AscFilterLegend'
import AscNoData from '@/components/atoms/AscNoData'
import AscChart from './AscChart'

export default {
  name: 'AscTimelineChart',

  components: {
    AscChart,
    AscSpinnerWrapper,
    AscNoData,
    AscFilterLegend,
  },

  props: {
    flags: {
      type: Array,
      default: () => [],
    },
    title: {
      type: String,
      default: '',
    },
    seriesName: {
      type: String,
      default: '',
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      legendItemsSelected: [],
      filteredData: [],
      defaultOptions: {
        chart: {
          zoomType: 'xy',
          pankey: 'ctrl',
          panning: true,
          type: 'timeline',
          height: '400',
        },
        xAxis: {
          type: 'datetime',
          visible: true,
        },
        yAxis: {
          gridLineWidth: 1,
          title: null,
          labels: {
            enabled: false,
          },
        },
        legend: {
          enabled: false,
        },
        title: {
          text: '',
        },
        tooltip: {
          useHTML: true,
          headerFormat: '<span></span>',
          pointFormat:
            '<div class="tooltip-container" style="background-color:{point.containerColor};"><div style="background-color:{point.backgroundColor};" class="tooltip-header"><span class="tooltip-header-text" style="color:{point.fontColor};">{point.removalType}</span></div>' +
            '<div class="tooltip-date" style="font-weight:bold;">{point.x:%d %b %Y}</div>' +
            '<div><strong>Part Number:</strong> {point.label}</div>' +
            '<div><strong>Serial #</strong>: {point.serialNumber}</div>' +
            '<div class="tooltip-details-text"><strong>Part Description:</strong> {point.partDescription}</div>' +
            '<div class="tooltip-details-text"><strong>Removal Reason:</strong> {point.removalReason}</div>' +
            '</div>',
          borderRadius: '0',
        },
        series: [],
      },
    }
  },

  computed: {
    isEmpty() {
      return this.filteredData?.length === 0 || this.flags.length === 0
    },

    getChartOptions() {
      if (this.filteredData.length > 0) {
        const series = this.buildSeries

        return {
          ...this.defaultOptions,
          ...series,
        }
      }

      return this.defaultOptions
    },

    buildSeries() {
      if (this.filteredData.length > 0) {
        const data = this.filteredData
          .map(item => {
            return {
              x: item.x,
              name: item.name,
              label: item.partNumber,
              partDescription: item.partDescription,
              serialNumber: item.serialNumber,
              removalReason: item.removalReason,
              color:
                item.removalType === 'S'
                  ? '#53b8b3'
                  : item.removalType === 'U'
                  ? '#848484'
                  : '#bebebe',
              fontColor: 'black',
              backgroundColor:
                item.removalType === 'S'
                  ? '#53b8b3'
                  : item.removalType === 'U'
                  ? '#848484'
                  : '#bebebe',
              removalType:
                item.removalType === 'S'
                  ? 'scheduled'
                  : item.removalType === 'U'
                  ? 'unscheduled'
                  : 'schedule is unidentified',
              timelineColor:
                item.removalType === 'S'
                  ? '#057874'
                  : item.removalType === 'U'
                  ? '#666'
                  : '#a6a6a6',
              containerColor:
                item.removalType === 'S'
                  ? '#fff'
                  : item.removalType === 'U'
                  ? '#e8e8e8'
                  : '#f7f7f7',
            }
          })
          .sort((a, b) => (a.x > b.x ? 1 : -1))
        return {
          series: [
            {
              dataLabels: {
                allowOverlap: true,
                format: `<div><span style="font-weight:bold;color:{point.timelineColor}">{point.x:%d %b %Y}</span></div>
                  <br/><br/>
                  <div><span style="font-weight:bold;color:{point.timelineColor}">Part Number:</span></div>
                  <br/>
                  <div><span style="font-weight:bold;color:{point.timelineColor};">{point.label}</span></div>`,
                borderRadius: '0',
              },
              marker: {
                symbol: 'circle',
              },
              tooltip: {
                format: '<span>{point.label}</span>',
              },
              data: data,
            },
          ],
        }
      }
      return []
    },

    getLegendItems() {
      return [
        {
          header: '',
          items: [
            {
              text: 'Scheduled',
              styleColor: '#53b8b3',
            },
            {
              text: 'Unscheduled',
              styleColor: '#848484',
            },
            {
              text: 'Unidentified',
              styleColor: '#a6a6a6',
            },
          ],
        },
      ]
    },
  },

  watch: {
    flags: {
      handler(newVal) {
        this.legendItemsSelected = []
        this.filteredData = newVal
      },
    },
  },

  methods: {
    handleLegendItemClick(data) {
      const isFound = this.legendItemsSelected.includes(data)
      if (isFound) {
        this.legendItemsSelected = this.legendItemsSelected.filter(
          item => item !== data,
        )
      } else {
        this.legendItemsSelected.push(data)
      }

      this.updateFilteredData()
    },

    updateFilteredData() {
      if (this.flags.length) {
        this.filteredData = this.flags.filter(item => {
          if (
            item.removalType === 'S' &&
            this.legendItemsSelected.includes('Scheduled')
          ) {
            return false
          }
          if (
            item.removalType === 'U' &&
            this.legendItemsSelected.includes('Unscheduled')
          ) {
            return false
          }
          if (
            item.removalType === null &&
            this.legendItemsSelected.includes('Unidentified')
          ) {
            return false
          }
          return true
        })
      }
    },
  },
}
</script>

<style lang="scss">
.asc-timeline-chart {
  .asc-timeline-chart--container {
    margin-top: 25px;

    .asc-highchart {
      overflow: inherit !important;
      overflow-y: scroll !important;

      .highcharts-reset-zoom {
        rect {
          fill: #fff;
          width: 90px;
        }

        text {
          text-transform: uppercase;
        }
      }
    }
  }

  .asc-no-data--container {
    min-height: 400px;
  }
}

.tooltip-container {
  width: 300px;

  .tooltip-header {
    padding: 4px;

    .tooltip-header-text {
      font-size: 14px;
      font-weight: bold;
      text-transform: uppercase;
    }
  }

  .tooltip-date {
    margin: 12px 0;
  }

  .tooltip-details-text {
    word-wrap: break-word;
    white-space: pre-wrap;
  }
}
</style>
